import { useCallback, useEffect, useState } from "react";
import Flex from "../../common/Flex";
import Slider from "../../common/Slider";
import Typography from "../../common/Typography";
import useDebounceCallback from "@/shared/hooks/useDebouncCallback";
import type { FilterComponent } from "../filter.types";
import config from "@/configs/config";
import { useSearchParams } from "next/navigation";

export type PriceRange = {
  min: number;
  max: number;
  step?: number;
  unit?: string;
};

const DEFAULT_PRICE_RANGE: PriceRange = {
  min: 0,
  max: 999999,
  step: 1,
  unit: "",
};

const PRICE_FILTER_DEBOUNCE_DELAY = 500;
const PRICE_SLIDER_STEP = 10000;

const PriceFilter: FilterComponent<PriceRange> = ({
  value,
  applyFilter,
  filterName,
  currentValue,
}) => {
  const defaultRange: PriceRange = {
    min: value?.min ?? DEFAULT_PRICE_RANGE.min,
    max: value?.max ?? DEFAULT_PRICE_RANGE.max,
    step: value?.step ?? DEFAULT_PRICE_RANGE.step,
    unit: value?.unit ?? DEFAULT_PRICE_RANGE.unit,
  };

  const currentState =
    currentValue || useSearchParams().get(filterName || "price");

  const [currentMin, currentMax] = currentState?.split("-") || "";

  const [priceRange, setPriceRange] = useState<number[]>([
    +currentMin || defaultRange.min,
    +currentMax || defaultRange.max,
  ]);

  // Use unit from value prop (defaultRange)
  const currencySymbol = defaultRange.unit;

  const debouncedFilterUpdate = useDebounceCallback((range: number[]) => {
    const filterValue = `${range[0]}-${range[1]}`;
    applyFilter(filterValue)();
  }, PRICE_FILTER_DEBOUNCE_DELAY);

  const updatePriceRange = (newRange: number[]) => {
    setPriceRange(newRange);
    debouncedFilterUpdate(newRange);
  };

  const resetPriceRange = useCallback(() => {
    setPriceRange([defaultRange.min, defaultRange.max]);
  }, [defaultRange.max, defaultRange.min]);

  useEffect(() => {
    if (!currentState) {
      resetPriceRange();
    }
  }, [currentState, resetPriceRange]);

  const formatPrice = (value: number): string =>
    `${value.toLocaleString()} ${currencySymbol}`;

  return (
    <Flex direction="col" className="py-5 w-full" gap="4">
      <Slider
        dir={config.dir}
        onValueChange={updatePriceRange}
        value={priceRange}
        min={defaultRange.min}
        max={defaultRange.max}
        step={defaultRange.step ?? PRICE_SLIDER_STEP}
        classNames={{
          range: "bg-primary",
          thumb: {
            root: "border-[3px] border-primary",
            values: "text-sm",
          },
        }}
      />
      <Flex direction="row" alignItems="center" justifyContent="between">
        <Typography variant="body3">{formatPrice(priceRange[0])}</Typography>
        <Typography variant="body3">{formatPrice(priceRange[1])}</Typography>
      </Flex>
    </Flex>
  );
};

export default PriceFilter;
